import { DateTime } from "luxon";

import INoteTag from "../V2/CustomerNotes/Types/INoteTag";
import { INoteTagContentBuilder } from "../V2/CustomerNotes/Types/INoteTagContentBuilder";
import SeriesSeparatorNoteTagsContentBuilder from "../V2/CustomerNotes/Types/SeriesSeparatorNoteTagsContentBuilder";

export class CustomerNote {
  id?: string;
  text: string;
  centerId: string;
  type: CustomerNoteType;
  createdAtUtc?: string;

  constructor(note?: Partial<CustomerNote>) {
    this.id = note?.id;
    this.text = note?.text || "";
    this.centerId = note?.centerId || "";
    this.type = note?.type || CustomerNoteType.Generic;
    this.createdAtUtc = note?.createdAtUtc;
  }
}

/** Type of customer note in the CRM. */
export enum CustomerNoteType {
  /** A generic note which is always visible on the customer profile. */
  Generic = 1,
  /** A note which alerts the user when viewing the customer history. */
  ShowOnHistory = 2,
  /** A note which alerts the user when the customer is checked-in. */
  ShowDuringCheckIn = 3,
  /** A note which alerts the user when booking an appointment for the customer. */
  ShowWhenBooking = 4,
  /** A note which alerts the user when accepting payment for the customer. */
  ShowWhenPayment = 5
}

/** A customer note from the CRM enhanced with additional logic props. */
export class EnhancedCustomerNote extends CustomerNote {
  /** A DateTime representation of the note creation date. */
  createdAt?: DateTime;
  /** A flag indicating that this note is a 'red flag' note, which means its type is one of the 'show on' options.
   */
  isRedFlag: boolean;
  /** Tags which will serve as the prefix to the note text based on changes to the customer model. */
  tags: INoteTag[];
  readonly noteTagBuilder: INoteTagContentBuilder;

  constructor(
    note?: CustomerNote,
    tags?: INoteTag[],
    noteTagBuilder: INoteTagContentBuilder = new SeriesSeparatorNoteTagsContentBuilder(" / ")
  ) {
    super(note);
    this.tags = tags || [];
    if (note?.createdAtUtc) this.createdAt = DateTime.fromISO(note.createdAtUtc);
    this.isRedFlag = this.type !== CustomerNoteType.Generic;
    this.noteTagBuilder = noteTagBuilder;
  }

  /** Transform to the base object by truncating enhanced properties. */
  static toBase(note: EnhancedCustomerNote): CustomerNote {
    const { id, text, centerId, type, createdAtUtc } = note;
    return { id, centerId, type, createdAtUtc, text };
  }

  /** Transform to the base object, constructing `text` from custom text and note tags. */
  static toFullBase(note: EnhancedCustomerNote): CustomerNote {
    const tagsValue = note.noteTagBuilder.getNoteTagContent(note.tags);
    const { id, text, centerId, type, createdAtUtc } = note;
    return { id, centerId, type, createdAtUtc, text: `${tagsValue ? `${tagsValue} ` : ""}${text.trim()}` };
  }

  /** Returns a flag indicating if there is a difference in content between notes. */
  static isChange(note1: EnhancedCustomerNote, note2: EnhancedCustomerNote): boolean {
    return (
      note1.text !== note2.text ||
      note1.noteTagBuilder.getNoteTagContent(note1.tags) !== note2.noteTagBuilder.getNoteTagContent(note2.tags)
    );
  }

  /** The comparator for sorting customer notes. Orders them by 'red-flag' type, then by date descending. */
  static Comparator(note1: EnhancedCustomerNote, note2: EnhancedCustomerNote): number {
    if (note1.isRedFlag && !note2.isRedFlag) return -1;
    if (!note1.isRedFlag && note2.isRedFlag) return 1;
    if (note1.createdAt && note2.createdAt) return note1.createdAt > note2.createdAt ? -1 : 1;
    return 1;
  }
}
